import {Injectable} from '@angular/core';

import {BehaviorSubject, map, Observable, of, Subject, switchMap, tap} from 'rxjs';

import {VehicleListFilterRequest} from '../../models/remote.request/VehicleListFilterRequest';
import {VehicleControlIndicator} from '../../models/domain/VehicleControlIndicator';
import {Vehicle} from '../../models/domain/Vehicle';
import {Driver} from '../../models/domain/Driver';
import {WebAccessService} from '../web.access/web-access.service';
import {VehicleControlIndicatorType} from '../../models/domain/VehicleControlIndicatorType';
import {SetSendToETollRequest} from '../../models/remote.request/SetSendToETollRequest';
import {UpdateVehicleNameRequest} from '../../models/remote.request/UpdateVehicleNameRequest';

import {NewWebSocketHttpService} from "@shared/websocket/new-web-socket-http.service";
import {NewDataStoreService} from "@shared/services/new-data-store.service";

@Injectable()
export class VehicleService {

  private followVehicleSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private listFilterSubject: BehaviorSubject<Vehicle[]> = new BehaviorSubject<Vehicle[]>([]);
  private listFilterCountSubject: BehaviorSubject<any> = new BehaviorSubject<any>({});
  private listFilterRequestSubject: Subject<VehicleListFilterRequest> = new Subject<VehicleListFilterRequest>();
  private sendVehicleChangeUpdate$: Subject<string> = new Subject<string>();
  private currentFilter: any;
  private currentFilterResult: any;

  constructor(
    private $webAccess: WebAccessService,
    private newDataStoreService: NewDataStoreService,
    private newWebSocketHttpService: NewWebSocketHttpService
  ) {
  }

  /**
   * inicjacja musi byc pozniej poniewaz w konstruktorze wywolanie serwisu do
   * zsynchronizowania danych juz te dane pobiera (uzywanie starych danych)
   */
  initDataFlow() {
    this.listFilterRequestSubject.pipe(
      switchMap(filter => {
        this.currentFilter = filter || {};
        return this.$webAccess.GetVehicleIdListByFilter(this.currentFilter);
      }),
      map(responses => {
        const response = <any>responses;
        // counter
        const counter = response ? response.Statuses as any : null;
        // wynik
        const result = response ? response.Result as number[] : null;
        // result
        return {vehicleIds: result, counter: counter};
      }))
      .subscribe(result => {
        this.currentFilterResult = result;
        this.sendVehicleChangeUpdate$.next('filter');
      });


    this.newWebSocketHttpService.updatedVehicles.subscribe(() => {
      this.sendVehicleChangeUpdate$.next('webSocket');
    });

    this.sendVehicleChangeUpdate$.asObservable().pipe(
      switchMap(result => this.getList$()),
      map(vehicles => {
        if (this.currentFilterResult && this.currentFilterResult.vehicleIds) {
          const joined = this.sortMergeJoin(vehicles, this.currentFilterResult.vehicleIds);
          return {vehicles: joined, counter: this.currentFilterResult.counter};
        }
        return {vehicles: vehicles, counter: null};
      }))
      .subscribe(result => {
        this.listFilterCountSubject.next(result);

        // przekazanie do wyfiltrownych
        this.listFilterSubject.next(result.vehicles);
      });
  }

  getFollowVehicleSubject(): Observable<boolean> {
    return this.followVehicleSubject.asObservable();
  }

  setFollowVehicleStatus(flag: boolean): void {
    this.followVehicleSubject.next(flag);
  }

  findVehicle(filter: VehicleListFilterRequest): void {
    this.listFilterRequestSubject.next(filter);
  }

  getCurrentDriver$(electronicId: string | number): Observable<Driver> {
    return of(this.newDataStoreService.getDriverByElectronicId(electronicId.toString()))
  }


  getDetails$(vehicleId: number): Observable<Vehicle> {
    return of(this.newDataStoreService.getVehicleById(vehicleId));
  }

  public getVehicleDetails(vehicleId: number): Vehicle {
    return this.newDataStoreService.getVehicleById(vehicleId);
  }

  getList$(): Observable<Vehicle[]> {
    return of(this.newDataStoreService.getVehicles());
  }

  getListFilteredCount$(): Observable<any> {
    return this.listFilterCountSubject.asObservable();
  }

  getListFiltered$(): Observable<Vehicle[]> {
    return this.listFilterSubject.asObservable();
  }

  refreshListFiltered(): void {
    this.sendVehicleChangeUpdate$.next('refresh');
  }

  getParameterList$(vehicleId: number): Observable<VehicleControlIndicator[]> {
    return of(this.newDataStoreService.getVehicleById(vehicleId))
      .pipe(
        // tap(x => console.table(x)),
        map(result => result && result.ControlIndicators ? result.ControlIndicators : []));
  }

  /**
   * pobranie listy wszystkich kontrolek
   */
  getVehicleControlIndicatorTypeList$(): Observable<VehicleControlIndicatorType[]> {
    const types: VehicleControlIndicatorType[] = [
      {Type: 1, Name: 'Adres'},
      {Type: 2, Name: 'Czas'},
      {Type: 3, Name: 'Czujniki'},
      {Type: 4, Name: 'Kierowca'},
      {Type: 5, Name: 'Licznik km'},
      {Type: 6, Name: 'Napięcie'},
      {Type: 7, Name: 'Obroty'},
      {Type: 8, Name: 'Paliwo'},
      //{ Type: 9, Name: 'Paliwo CAN' },
      {Type: 10, Name: 'Pedał gazu'},
      {Type: 11, Name: 'Pojazd'},
      {Type: 12, Name: 'Prędkość'},
      {Type: 13, Name: 'Status'},
      {Type: 14, Name: 'Sygnał GPS'},
      {Type: 15, Name: 'Sygnał GSM'},
      {Type: 16, Name: 'Tachograf'},
      {Type: 17, Name: 'Temperatura'},
      {Type: 18, Name: 'Zuzycie CAN'},
      {Type: 19, Name: 'eToll'},
    ];
    return of(types);
  }

  /**
   * Applies sort-merge join on vehicle ids on two given arrays (15x faster than .indexOf !== -1)
   * @param vehicles array of vehicles
   * @param vehicleIds array of vehicle ids
   */
  private sortMergeJoin(vehicles: Vehicle[], vehicleIds: number[]): Vehicle[] {

    vehicles.sort((x, y) => x.Id! - y.Id!);
    vehicleIds.sort((x, y) => x - y);

    let i = 0;
    let j = 0;
    const joined: Vehicle[] = [];

    while (i < vehicles.length && j < vehicleIds.length) {

      if (vehicles[i].Id === vehicleIds[j]) {
        joined.push(vehicles[i]);
        i += 1;
        j += 1;
      } else if (vehicles[i].Id! < vehicleIds[j]) {
        i += 1;
      } else {
        j += 1;
      }

    }

    return joined;
  }

  /**
   * włączenie/wyłączenie wysyłania danych do usługi e-Toll
   */
  setSendToEToll(sendToEToll: SetSendToETollRequest): Observable<void> {
    return this.$webAccess.SetSendToEToll(sendToEToll)
      .pipe(map(response => response.Result));
  }

  /**
   * pobranie informacji o wysyłaniu danych do usługi e-Toll
   */
  getSendToEToll(idVehicle: number): Observable<boolean> {
    return this.$webAccess.GetSendToEToll(idVehicle)
      .pipe(map(response => response.Result.SendToEToll));
  }

  updateVehicleName(updateVehicleNameRequest: UpdateVehicleNameRequest): Observable<{}> {
    return this.$webAccess.UpdateVehicleName(updateVehicleNameRequest)
      .pipe(
        map(response => response.Result.SendToEToll),
        switchMap(() => {
          return of(this.newDataStoreService.getVehicleById(updateVehicleNameRequest.IdVehicle))
        }),
        tap(vehicleFromDb => {
          this.newDataStoreService.addOrUpdateVehicle(vehicleFromDb);
        }))
  }

}
